import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { VideoHero } from '@/components/VideoHero';
import { FilterPopover } from '@/components/homepage/FilterPopover';
import { Button } from '@/components/ui/button';
import { Building2, ArrowRight, ChevronDown } from 'lucide-react';
import useHomePageStore from '@/store/useHomePageStore';
import { useTranslation } from 'react-i18next';

interface HeroSectionProps {
  /** Handler for search functionality */
  onSearch?: (query: string, coordinates?: [number, number]) => Promise<void>;
  /** Additional CSS classes for styling */
  className?: string;
  /** Hide search functionality */
  hideSearch?: boolean;
  /** Hide filter functionality */
  hideFilters?: boolean;
  /** Custom content to render instead of default content */
  customContent?: React.ReactNode;
  /** Video source URL for the background video */
  videoSrc?: string;
  /** Custom translation namespace (defaults to 'homepage.hero') */
  translationNamespace?: string;
  /** Custom title content */
  customTitle?: React.ReactNode;
  /** Custom subtitle content */
  customSubtitle?: React.ReactNode;
  /** Custom CTA buttons */
  customCTA?: React.ReactNode;
  /** Show decorative background elements */
  showDecorations?: boolean;
  /** Show business CTA section */
  showBusinessCTA?: boolean;
  /** Fill the first screen, leaving room for the search strip docked below it */
  fullScreen?: boolean;
  /** Shows a cue at the bottom of the hero that moves on to the search section */
  onExplore?: () => void;
}

/**
 * HeroSection Component
 * 
 * Displays the main hero section with video background, search functionality,
 * and filtering options. Can be customized for different pages.
 */
export function HeroSection({
  onSearch = async () => {},
  className,
  hideSearch = false,
  hideFilters = false,
  customContent,
  videoSrc = "/video/hero-section-whale-video-background.mp4", // Default video
  translationNamespace = 'homepage.hero',
  customTitle,
  customSubtitle,
  customCTA,
  showDecorations = false,
  showBusinessCTA = true,
  fullScreen = false,
  onExplore
}: HeroSectionProps) {
  const { t } = useTranslation('public', { keyPrefix: translationNamespace });
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  
  // Get filter state from global store
  const {
    activityFilters,
    selectedAnimalTypes,
    selectedAdventureTypes,
    additionalFilters,
    setActivityFilters,
    setSelectedAnimalTypes,
    setSelectedAdventureTypes,
    setSelectedSeason,
    setAdditionalFilters,
  } = useHomePageStore();

  const handleClearFilters = () => {
    setActivityFilters({
      showAnimalActivities: true,
      showAdventureActivities: true
    });
    setSelectedAnimalTypes([]);
    setSelectedAdventureTypes([]);
    setSelectedSeason(null);
    setAdditionalFilters({
      ...additionalFilters,
      groupSize: [1, 20]
    });
  };

  const handleGroupSizeChange = (size: [number, number]) => {
    setAdditionalFilters({
      ...additionalFilters,
      groupSize: size
    });
  };

  // Only count filters that actually affect results (exclude Season and Group size until backend supports them)
  const hasActiveFilters = 
    selectedAnimalTypes.length > 0 || 
    selectedAdventureTypes.length > 0 ||
    (!activityFilters.showAnimalActivities || !activityFilters.showAdventureActivities);

  return (
    <div className={className}>
      <VideoHero
        videoSrc={videoSrc}
        // svh, not dvh: the hero must not jump as a phone's browser bar hides
        height={fullScreen ? 'calc(100svh - 4.0625rem)' : undefined}
        footer={onExplore && (
          <button
            type="button"
            onClick={onExplore}
            className="flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-sm font-semibold text-white/90 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            {t('explore')}
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
          </button>
        )}
      >
        {customContent || (
          <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-0">
            {/* Decorative Elements */}
            {showDecorations && (
              <motion.div
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: shouldReduceMotion ? 0 : 1 }}
              >
                <div className="absolute top-20 left-10 w-72 h-72 bg-green-100/20 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-100/15 rounded-full blur-3xl animate-pulse delay-1000" />
                <div className="absolute top-40 right-1/4 w-64 h-64 bg-emerald-100/20 rounded-full blur-3xl animate-pulse delay-500" />
              </motion.div>
            )}

            {/* Hero Title */}
            <motion.h1 
              className="mt-12 sm:mt-24 text-center mx-auto text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 tracking-tight leading-tight sm:leading-[0.9]"
              style={{
                textShadow: '0 4px 20px rgba(0,0,0,0.5), 0 0 40px rgba(0,0,0,0.3)'
              }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: shouldReduceMotion ? 0 : 0.8, ease: "easeOut" }}
            >
              {customTitle || (
                <>
                  {t('title.discover')}
                  <br />
                  <span className="bg-gradient-to-r from-emerald-200 to-emerald-300 bg-clip-text text-transparent">
                    {t('title.adventures')}
                  </span>
                </>
              )}
            </motion.h1>

            {/* Hero Subtitle */}
            <motion.p 
              className="mb-8 text-center text-xl sm:text-2xl text-white/95 max-w-3xl mx-auto leading-relaxed font-light"
              style={{
                textShadow: '0 2px 15px rgba(0,0,0,0.5)'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: shouldReduceMotion ? 0 : 0.6, delay: shouldReduceMotion ? 0 : 0.1, ease: "easeOut" }}
            >
              {customSubtitle || (
                <>
                  {t('subtitle.main')}
                  <span className="hidden sm:block mt-2 text-lg text-green-100">
                    {t('subtitle.conservation')}
                  </span>
                </>
              )}
            </motion.p>

            {/* Search is in the sticky strip below hero (HomePage) on all viewports so it sticks when scrolling. */}
            {!hideSearch && <div className="h-2 md:h-3" aria-hidden />}
            <div className="flex justify-center mt-2 sm:mt-4"></div>
            {/* Filter Popover */}
            {!hideFilters && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: shouldReduceMotion ? 0 : 0.5, delay: shouldReduceMotion ? 0 : 0.3 }}
                className="flex flex-col gap-6"
              >
                <FilterPopover
                  activityFilters={activityFilters}
                  selectedAnimalTypes={selectedAnimalTypes}
                  selectedAdventureTypes={selectedAdventureTypes}
                  selectedSeason={additionalFilters.selectedSeason}
                  groupSize={additionalFilters.groupSize}
                  onActivityFilterChange={setActivityFilters}
                  onAnimalTypeChange={setSelectedAnimalTypes}
                  onAdventureTypeChange={setSelectedAdventureTypes}
                  onSeasonChange={setSelectedSeason}
                  onGroupSizeChange={handleGroupSizeChange}
                  onClearFilters={handleClearFilters}
                  showClearFilters={hasActiveFilters}
                />
              </motion.div>
            )}

            {/* Custom CTA or Business CTA Section */}
            {customCTA ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: shouldReduceMotion ? 0 : 0.5, delay: shouldReduceMotion ? 0 : 0.3 }}
                className="flex justify-center gap-4 mt-4 sm:mt-6"
              >
                {customCTA}
              </motion.div>
            ) : showBusinessCTA ? (
              <div className="hidden sm:block">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: shouldReduceMotion ? 0 : 0.5, delay: shouldReduceMotion ? 0 : 0.4 }}
                  className="flex justify-center mt-4 sm:mt-6"
                >
                  <div className="relative">
                  {/* Glass morphism container for business CTA */}
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-4 py-3 sm:px-6 sm:py-4 shadow-lg shadow-black/10">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                      {/* Text content */}
                      <div className="flex items-center gap-2 text-white/90 text-center sm:text-left min-w-0">
                        <Building2 className="w-5 h-5 text-emerald-300 shrink-0" />
                        <span className="text-sm sm:text-base font-medium">
                          {t('business.cta.title')}
                          <span className="hidden sm:inline"> | </span>
                          <span className="block sm:inline text-white/75 text-sm">
                            {t('business.cta.description')}
                          </span>
                        </span>
                      </div>
                      
                      {/* CTA button */}
                      <Button
                        onClick={() => navigate('/for-business')}
                        size="sm"
                        className="min-h-[44px] bg-white/20 border border-white/40 text-white hover:bg-white/30 hover:border-white/50 shadow-sm hover:shadow-md transition-all duration-200 px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm backdrop-blur-sm group"
                      >
                        {t('business.cta.action')}
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
              </div>
            ) : null}
          </div>
        )}
      </VideoHero>
    </div>
  );
} 